import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import './swagger-theme.css';

import { Button } from 'components/ui/button';
import { API_DOCS_URL, API_OPENAPI_URL } from '../../config/constants';
import { ApiDocsProps, SwaggerRequest } from '../../types/api-docs.types';

export const ApiDocs: FC<ApiDocsProps> = ({ authToken }) => {
  const { t: translation } = useTranslation();
  const [hasSpecError, setHasSpecError] = useState(false);

  useEffect(() => {
    // Swagger UI's built-in dark theme is keyed on <html class="dark-mode">
    document.documentElement.classList.add('dark-mode');

    return () => {
      document.documentElement.classList.remove('dark-mode');
    };
  }, []);

  useEffect(() => {
    const { pathname, search } = window.location;
    const originalPushState = window.history.pushState.bind(window.history);

    window.history.pushState = (data, unused, url) => {
      if (typeof url === 'string' && url.startsWith('#')) {

        return originalPushState(data, unused, `${pathname}${search}${url}`);
      }

      return originalPushState(data, unused, url);
    };

    return () => {
      window.history.pushState = originalPushState;
    };
  }, []);

  const requestInterceptor = (request: SwaggerRequest) => {
    const token = authToken || localStorage.getItem('accessToken');

    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }

    return request;
  };

  if (hasSpecError) {
    return (
      <div className="space-y-3 rounded-2xl border border-ob-border bg-ob-shell p-6">
        <p className="text-sm text-ob-text-muted">
          {translation('apiPage.docs unavailable')}
        </p>
        <Button variant="ob-outline" size="pill-sm" asChild>
          <a href={API_DOCS_URL} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={16} />
            {translation('apiPage.open docs')}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="ob-swagger overflow-hidden rounded-2xl border border-ob-border bg-ob-shell pb-6">
      <SwaggerUI
        url={API_OPENAPI_URL}
        docExpansion="list"
        defaultModelsExpandDepth={-1}
        deepLinking={false}
        queryConfigEnabled={false}
        requestInterceptor={requestInterceptor}
        onFailure={() => setHasSpecError(true)}
      />
    </div>
  );
};
